import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { ChevronRightIcon } from 'lucide-react';
import { Container, Section, Kicker, Pill, Callout } from '../components/ui/Primitives';
import { Button } from '../components/ui/Button';
import { SoftGate } from '../components/sections/SoftGate';
import { Seo } from '../components/Seo';
import { articleSchema, breadcrumbSchema } from '../lib/seo';
import { getArticle, ARTICLES } from '../data/insights';
import { SERVICE_LINES } from '../data/services';
import { fetchReaderUnlocked, setReaderUnlocked } from '../lib/leads';
import { usePortalAuth } from '../context/PortalAuth';
import { NotFound } from './NotFound';
export function ArticlePage() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const article = getArticle(slug ?? '');
  const {
    user
  } = usePortalAuth();
  const reduced = useReducedMotion();
  const [unlocked, setUnlocked] = React.useState(false);
  React.useEffect(() => {
    let active = true;
    void fetchReaderUnlocked().then(value => {
      if (active) setUnlocked(value);
    });
    return () => {
      active = false;
    };
  }, [slug]);
  if (!article) return <NotFound data-id="element-572" data-mp="2ly5gaf5lxda5" />;
  const gateApplies = article.gated && !unlocked && !user;
  const teaser = article.body.slice(0, article.teaserCut);
  const remainder = article.body.slice(article.teaserCut);
  const related = ARTICLES.filter(item => item.slug !== article.slug).slice(0, 3);
  const lineName = SERVICE_LINES.find(line => line.id === article.serviceLine)?.name;
  const wordCount = article.body.reduce((total, section) => total + section.paragraphs.reduce((count, paragraph) => count + paragraph.split(/\s+/).length, 0), 0);
  const onUnlock = () => {
    setReaderUnlocked();
    setUnlocked(true);
  };
  return <>
      <Seo title={article.title} description={article.excerpt} path={`/insights/${article.slug}`} image={article.heroImage} type="article" article={{
      publishedAt: article.publishedAt,
      author: article.author.name,
      section: lineName,
      tags: article.sectors
    }} jsonLd={[articleSchema({
      title: article.title,
      description: article.excerpt,
      path: `/insights/${article.slug}`,
      image: article.heroImage,
      publishedAt: article.publishedAt,
      author: article.author,
      section: lineName,
      about: article.sectors,
      wordCount,
      readTime: article.readTime,
      summary: article.summaryBlock
    }), breadcrumbSchema([{
      name: 'Home',
      path: '/'
    }, {
      name: 'Insights',
      path: '/insights'
    }, {
      name: article.title,
      path: `/insights/${article.slug}`
    }])]} data-id="element-573" data-mp="1agxbvtuynx5z" />

      <section className="surface-dark" data-id="element-574" data-mp="gtax1skpdw1t">
        <Container className="py-12 sm:py-16" data-id="element-575" data-mp="gs9aory8ln4s">
          <nav aria-label="Breadcrumb" className="mb-8" data-id="element-576" data-mp="21blg7l677m3i">
            <ol className="flex flex-wrap items-center gap-2 text-small text-white/55" data-id="element-577" data-mp="21b1wq00wgpnd">
              <li data-id="element-578" data-mp="1wda5z9bzr9kr"><Link to="/" className="hover:text-white" data-id="element-579" data-mp="1wda4f4wcf1in">Home</Link></li>
              <li aria-hidden="true" data-id="element-580" data-mp="1rl09tbka9da1"><ChevronRightIcon className="h-3.5 w-3.5" data-id="element-581" data-mp="1rkh3ks3zwdbu" /></li>
              <li data-id="element-582" data-mp="1mngt85044oex"><Link to="/insights" className="hover:text-white" data-id="element-583" data-mp="1mnqnw5w3rjqy">Insights</Link></li>
              <li aria-hidden="true" data-id="element-584" data-mp="1hnm1y0cfdwtc"><ChevronRightIcon className="h-3.5 w-3.5" data-id="element-585" data-mp="1hox826tcfgnm" /></li>
              <li aria-current="page" className="truncate text-white/80" data-id="element-586" data-mp="1dmadvz979jn9">{article.title}</li>
            </ol>
          </nav>
          <Kicker data-id="element-587" data-mp="18me75s94be40">{lineName}</Kicker>
          <h1 className="mt-4 max-w-3xl font-display text-[30px] leading-tight text-white sm:text-[42px]" data-id="element-588" data-mp="18npjwhkjjw79">
            {article.title}
          </h1>
          <p className="mt-6 text-small text-white/55" data-id="element-589" data-mp="330yrg3tj17mv">
            {article.author.name}, {article.author.role} ·{' '}
            {new Date(article.publishedAt).toLocaleDateString('en-GB', {
            day: 'numeric',
            month: 'long',
            year: 'numeric'
          })}{' '}
            · {article.readTime} minute read
          </p>
        </Container>
      </section>

      <Container className="py-10 sm:py-14" data-id="element-590" data-mp="1lqs4niagczth">
        <img src={article.heroImage} alt={article.heroAlt} className="aspect-[16/7] w-full rounded-md object-cover" data-id="element-591" data-mp="1lq09u9ooj649" />

        <div className="mt-8 flex flex-wrap gap-2" data-id="element-592" data-mp="1c070zgfx953p">
          {article.sectors.map(sector => <Pill key={sector} data-id="element-593" data-mp="1gryu9yqgpoxj">{sector}</Pill>)}
        </div>

        <div className="mt-10 max-w-prose" data-id="element-594" data-mp="1gt9yu0rqf0pp">
          <Callout data-id="element-595" data-mp="1313h4a8xklo5">
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted" data-id="element-596" data-mp="131ln3yleae99">In short</p>
            <p className="mt-2 text-[15px] text-body" data-id="element-597" data-mp="17zdbwjqqtk98">{article.summaryBlock}</p>
          </Callout>
        </div>

        <article className="mt-10 max-w-prose" data-id="element-598" data-mp="t3xo2wgrakla">
          {teaser.map((section, index) => <section key={index} data-id="element-599" data-mp="y31xno7lekc5" data-mpi={index}>
              {section.heading && <h2 className="mb-4 mt-10 font-display text-[24px] leading-snug text-ink first:mt-0" data-id="element-600" data-mp="efxdsaun5zea">
                  {section.heading}
                </h2>}
              {section.paragraphs.map((paragraph, pIndex) => <p key={pIndex} className="mb-5 text-[17px] leading-[1.7] text-body" data-id="element-601" data-mp="oaz2j5l08zgc" data-mpi={pIndex}>
                  {paragraph}
                </p>)}
            </section>)}

          {/* The remaining content stays in the served markup for indexing. The gate is a client side reveal only. */}
          <div className={gateApplies ? 'pointer-events-none max-h-64 select-none overflow-hidden' : ''} aria-hidden={gateApplies} data-id="element-602" data-mp="173tia1qgcvlm">
            {remainder.map((section, index) => <motion.section key={index} initial={reduced || gateApplies ? false : {
            opacity: 0,
            y: 8
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.35,
            ease: 'easeOut',
            delay: index * 0.03
          }} data-id="element-603" data-mp="126t9vk64rgrc" data-mpi={index}>
                {section.heading && <h2 className="mb-4 mt-10 font-display text-[24px] leading-snug text-ink" data-id="element-604" data-mp="1o4xnd9jtp0kh">{section.heading}</h2>}
                {section.paragraphs.map((paragraph, pIndex) => <p key={pIndex} className="mb-5 text-[17px] leading-[1.7] text-body" data-id="element-605" data-mp="1xuqothq93zb0" data-mpi={pIndex}>
                    {paragraph}
                  </p>)}
              </motion.section>)}
          </div>

          {gateApplies && <SoftGate article={article} onUnlock={onUnlock} data-id="element-606" data-mp="26udqm4mw774l" />}
        </article>

        {!gateApplies && <div className="mt-12 max-w-prose rounded-md border border-line bg-mist p-6 sm:p-8" data-id="element-607" data-mp="2bvb6ubocdr3g">
            <h2 className="font-display text-[22px] text-ink" data-id="element-608" data-mp="2gsjtopkfqjb1">Working on something similar</h2>
            <p className="mt-3 text-[15px] text-body" data-id="element-609" data-mp="hi24r4y4l7v8">
              A discovery call lasts thirty minutes and costs nothing. Tell us the decision in front of you.
            </p>
            <div className="mt-6 flex flex-col gap-3 sm:flex-row" data-id="element-610" data-mp="ewklc06pel7q">
              <Button to="/contact" data-id="element-611" data-mp="a5m48ydw5j3f">Book a discovery call</Button>
              <Button to="/assessment" variant="secondary" data-id="element-612" data-mp="a4b190s9sfdd">Take the assessment</Button>
            </div>
          </div>}
      </Container>

      <Section tone="mist" aria-labelledby="related-articles" data-id="element-613" data-mp="7ohygfldwdg">
        <h2 id="related-articles" className="font-display text-[26px] text-ink" data-id="element-614" data-mp="5bqncny29nq">
          Related reading
        </h2>
        <div className="mt-8 grid gap-8 sm:grid-cols-3" data-id="element-615" data-mp="sw3u8v7ca0l3">
          {related.map(item => <article key={item.slug} data-id="element-616" data-mp="su94n3l4hkao">
              <p className="text-small uppercase tracking-[0.12em] text-gold" data-id="element-617" data-mp="nw7n4n3cht1b">{item.sectors[0]}</p>
              <h3 className="mt-3 font-display text-[19px] leading-snug text-ink" data-id="element-618" data-mp="iypgx4gpprx0">
                <Link to={`/insights/${item.slug}`} className="underline-offset-4 hover:underline" data-id="element-619" data-mp="38nrf3s92w2nj">
                  {item.title}
                </Link>
              </h3>
              <p className="mt-3 text-[15px] text-body" data-id="element-620" data-mp="33qhqufxfdx1a">{item.excerpt}</p>
            </article>)}
        </div>
      </Section>
    </>;
}